import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {eventOneClickPromotionPanelExtraReducers} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel-extra-reducers';
import type {
  EventOneClickPromoBackendResponse,
  EventOneClickPromoReduxState} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel.types';
import {
  OneClickPromoBlockedReason,
  OneClickPromoChannel,
  OneClickPromoItemState,
} from '@Panels/event-one-click-promotion-panel/event-one-click-promotion-panel.types';
import {getDummyEventItem} from '@Libraries/events.library';
import {LoadingStates} from '@Utils/loading.util';
import {getDummySocialPostForSkeleton} from '@Libraries/social-media-grid.library';
import {canUserSendOutEmailCampaign, getDummyEmailCampaign} from '@Libraries/email-campaigns-library';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isAccountPlanLocked} from '@Components/social-media/account.vo';
import type {MailingListVO} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import type {SocialPost} from '@Components/social-media/post.vo';
import {isOneClickEmailCampaignInSuccessState, isOneClickSocialPostInErrorState, isOneClickSocialPostInSuccessState} from '@Libraries/event-promotion.library';

const initialState: EventOneClickPromoReduxState = {
  event: getDummyEventItem(),
  loadingState: LoadingStates.NOT_LOADED,
  socialPost: getDummySocialPostForSkeleton(),
  campaign: getDummyEmailCampaign(),
  emailPublishFailMessage: '',
  postPublishFailMessage: '',
  showCampaignsLoader: false,
  showPostLoader: false,
  allSocialAccounts: [],
  allMailingLists: [],
  campaignOneClickState: OneClickPromoItemState.DEFAULT,
  socialPostOneCLickState: OneClickPromoItemState.DEFAULT,
  emailSends: 0,
  doesUserHaveSenderProfile: false,
  numConnectedParentAccounts: 0,
  isSocialSelected: true,
  isEmailSelected: true,
  autoDeselectedChannels: [],
  isPostFormatSelected: true,
  isStoryFormatSelected: true
};

/** An already-published campaign is never blocked: its prerequisites were met when it went out. */
const getEmailBlockedReason = (state: EventOneClickPromoReduxState): OneClickPromoBlockedReason | null => {
  if (isOneClickEmailCampaignInSuccessState(state.campaign, state.campaignOneClickState)) {
    return null;
  }

  if (!state.doesUserHaveSenderProfile) {
    return OneClickPromoBlockedReason.NO_SENDER_PROFILE;
  }

  if (!canUserSendOutEmailCampaign(state.emailSends, state.campaign)) {
    return OneClickPromoBlockedReason.INSUFFICIENT_EMAIL_SENDS;
  }

  if (state.campaign.mailingLists.length === 0) {
    return OneClickPromoBlockedReason.NO_MAILING_LIST;
  }

  return null;
};

/** An already-published post is never blocked: its prerequisites were met when it went out. */
const getSocialBlockedReason = (state: EventOneClickPromoReduxState): OneClickPromoBlockedReason | null => {
  if (isOneClickSocialPostInSuccessState(state.socialPost, state.socialPostOneCLickState)) {
    return null;
  }

  if (state.socialPost.accounts.length === 0) {
    return OneClickPromoBlockedReason.NO_SOCIAL_ACCOUNT;
  }

  return null;
};

/** Why the channel cannot publish, or null when it can. */
export const selectBlockedReasonForChannel = (state: EventOneClickPromoReduxState, channel: OneClickPromoChannel): OneClickPromoBlockedReason | null => {
  return channel === OneClickPromoChannel.EMAIL ? getEmailBlockedReason(state) : getSocialBlockedReason(state);
};

export const selectIsChannelSelected = (state: EventOneClickPromoReduxState, channel: OneClickPromoChannel): boolean => {
  return channel === OneClickPromoChannel.EMAIL ? state.isEmailSelected : state.isSocialSelected;
};

/** Ticked and able to publish: what the Publish button may act on. */
export const selectIsChannelTickedAndPublishable = (state: EventOneClickPromoReduxState, channel: OneClickPromoChannel): boolean => {
  return selectIsChannelSelected(state, channel) && selectBlockedReasonForChannel(state, channel) === null;
};

const setChannelSelected = (state: EventOneClickPromoReduxState, channel: OneClickPromoChannel, isSelected: boolean): void => {
  if (channel === OneClickPromoChannel.EMAIL) {
    state.isEmailSelected = isSelected;
  } else {
    state.isSocialSelected = isSelected;
  }
};

/** A user's own tick or untick is theirs: the channel is no longer one the block unticked. */
const forgetAutoDeselection = (state: EventOneClickPromoReduxState, channel: OneClickPromoChannel): void => {
  state.autoDeselectedChannels = state.autoDeselectedChannels.filter((autoDeselectedChannel) => {
    return autoDeselectedChannel !== channel;
  });
};

const eventOneClickPromotionPanelSlice = createSlice({
  name: 'eventOneClickPromo',
  initialState,
  reducers: {
    setOneClickDataForEvent: (state, {payload}: PayloadAction<EventOneClickPromoBackendResponse>): void => {
      state.event = payload.event;
      state.campaign = payload.campaign;
      state.socialPost = {...payload.socialPost, accounts: payload.socialPost.accounts.filter((account) => !isAccountPlanLocked(account))};
      state.allSocialAccounts = payload.accounts;
      state.allMailingLists = payload.mailingLists;
      state.emailSends = payload.emailSends;
      state.doesUserHaveSenderProfile = payload.doesUserHaveSenderProfile;
      state.numConnectedParentAccounts = payload.numParentAccountsConnected;
    },

    setSocialPostCaption: (state, action: PayloadAction<string>): void => {
      state.socialPost.caption = action.payload;
    },

    setEmailCampaignSubject: (state, action: PayloadAction<string>): void => {
      state.campaign.subject = action.payload;
    },

    setLoadingState: (state, action: PayloadAction<LoadingStates>): void => {
      state.loadingState = action.payload;
    },

    addAccountToOneClickPost: (state, action: PayloadAction<SocialAccount>): void => {
      if (!isAccountPlanLocked(action.payload)) {
        state.socialPost.accounts.push(action.payload);
      }
    },

    removeAccountFromOneClickPost: (state, action: PayloadAction<SocialAccount>): void => {
      state.socialPost.accounts = state.socialPost.accounts.filter((account) => {
        return account.id !== action.payload.id;
      });
    },

    addMailingListToOneClickCampaign: (state, action: PayloadAction<MailingListVO>): void => {
      state.campaign.mailingLists.push(action.payload);
    },

    removeMailingListFromOneClickCampaign: (state, action: PayloadAction<MailingListVO>): void => {
      state.campaign.mailingLists = state.campaign.mailingLists.filter((mailingList) => {
        return action.payload.id !== mailingList.id;
      });
    },

    setShowCampaignLoader: (state, action: PayloadAction<boolean>): void => {
      state.showCampaignsLoader = action.payload;
    },

    setShowSocialPostsLoader: (state, action: PayloadAction<boolean>): void => {
      state.showPostLoader = action.payload;
    },

    setEmailCampaign: (state, action: PayloadAction<EmailCampaign>): void => {
      state.campaign = action.payload;
    },

    setSocialPost: (state, action: PayloadAction<SocialPost>): void => {
      state.socialPost = action.payload;
    },

    setSocialPostPreviewUrlForPage: (state, action: PayloadAction<{posterHashedId: string; pageHashedId: string; url: string}>): void => {
      const {posterHashedId, pageHashedId, url} = action.payload;
      state.socialPost.posterPreviewUrls ??= {};
      state.socialPost.posterPreviewUrls[posterHashedId] ??= {};
      state.socialPost.posterPreviewUrls[posterHashedId][pageHashedId] = url;
    },

    setCampaignOneClickState: (state, action: PayloadAction<OneClickPromoItemState>): void => {
      state.campaignOneClickState = action.payload;
    },

    setSocialPostOneClickState: (state, action: PayloadAction<OneClickPromoItemState>): void => {
      state.socialPostOneCLickState = action.payload;
    },

    setEmailCampaignErrorMessage: (state, action: PayloadAction<string>): void => {
      state.emailPublishFailMessage = action.payload;
    },

    doResetOneClickState: (state): void  => {
      state.campaignOneClickState = OneClickPromoItemState.DEFAULT;
      state.socialPostOneCLickState = OneClickPromoItemState.DEFAULT;
      state.postPublishFailMessage = '';
      state.emailPublishFailMessage = '';
      state.isSocialSelected = initialState.isSocialSelected;
      state.isEmailSelected = initialState.isEmailSelected;
      state.autoDeselectedChannels = [];
      state.isPostFormatSelected = initialState.isPostFormatSelected;
      state.isStoryFormatSelected = initialState.isStoryFormatSelected;
    },

    addMailingListForOneClickFlow: (state, action: PayloadAction<MailingListVO>): void => {
      state.allMailingLists.push(action.payload);
    },

    addSocialAccountForOneClickFlow: (state, action: PayloadAction<SocialAccount>): void => {
      state.allSocialAccounts.push(action.payload);
    },

    setSocialAccountsForOneClickFlow: (state, action: PayloadAction<SocialAccount[]>): void => {
      state.allSocialAccounts = action.payload;
    },

    setUserHasSenderProfile: (state): void  => {
      state.doesUserHaveSenderProfile = true;
    },

    setIsSocialSelected: (state, action: PayloadAction<boolean>): void => {
      state.isSocialSelected = action.payload;
      forgetAutoDeselection(state, OneClickPromoChannel.SOCIAL);
    },

    setIsEmailSelected: (state, action: PayloadAction<boolean>): void => {
      state.isEmailSelected = action.payload;
      forgetAutoDeselection(state, OneClickPromoChannel.EMAIL);
    },

    /** Unticks a channel that became blocked, and re-ticks it once the block clears if the block was what unticked it. */
    syncChannelSelectionWithBlock: (state, action: PayloadAction<OneClickPromoChannel>): void => {
      const channel = action.payload;
      const isBlocked = selectBlockedReasonForChannel(state, channel) !== null;

      if (isBlocked && selectIsChannelSelected(state, channel)) {
        setChannelSelected(state, channel, false);
        state.autoDeselectedChannels.push(channel);
        return;
      }

      if (!isBlocked && state.autoDeselectedChannels.includes(channel)) {
        setChannelSelected(state, channel, true);
        forgetAutoDeselection(state, channel);
      }
    },

    setIsPostFormatSelected: (state, action: PayloadAction<boolean>): void => {
      if (!action.payload && !state.isStoryFormatSelected) {
        return;
      }

      state.isPostFormatSelected = action.payload;
    },

    setIsStoryFormatSelected: (state, action: PayloadAction<boolean>): void => {
      if (!action.payload && !state.isPostFormatSelected) {
        return;
      }

      state.isStoryFormatSelected = action.payload;
    }
  },
  extraReducers: eventOneClickPromotionPanelExtraReducers,
});

export const {
  setSocialPostCaption,
  setEmailCampaignSubject,
  setOneClickDataForEvent,
  setLoadingState,
  addAccountToOneClickPost,
  removeAccountFromOneClickPost,
  addMailingListToOneClickCampaign,
  removeMailingListFromOneClickCampaign,
  setShowSocialPostsLoader,
  setShowCampaignLoader,
  setSocialPost,
  setSocialPostPreviewUrlForPage,
  setEmailCampaign,
  setCampaignOneClickState,
  setSocialPostOneClickState,
  setEmailCampaignErrorMessage,
  doResetOneClickState,
  addMailingListForOneClickFlow,
  addSocialAccountForOneClickFlow,
  setSocialAccountsForOneClickFlow,
  setUserHasSenderProfile,
  setIsSocialSelected,
  setIsEmailSelected,
  syncChannelSelectionWithBlock,
  setIsPostFormatSelected,
  setIsStoryFormatSelected
} = eventOneClickPromotionPanelSlice.actions;
export const eventOneClickPromotionReducer = eventOneClickPromotionPanelSlice.reducer;

/** The publish button is dead only when neither channel is going out. */
export const selectCanPublish = (state: EventOneClickPromoReduxState): boolean => {
  return state.isSocialSelected || state.isEmailSelected;
};

/** Only a channel that reached SUCCESS inside this panel session counts as something the user just published. */
const hasAnyChannelPublishedThisSession = (state: EventOneClickPromoReduxState): boolean => {
  return state.socialPostOneCLickState === OneClickPromoItemState.SUCCESS || state.campaignOneClickState === OneClickPromoItemState.SUCCESS;
};

/**
 * The panel lands on the success state once something went out in this session AND every channel the
 * user selected is in a success state. The session check keeps a re-promoted event off the success
 * screen on open; reading the selected channels through the library helpers lets a channel that was
 * already published — and whose tile therefore cannot be deselected — stop holding the other one back.
 * A deselected channel drops out entirely and with nothing selected there is no publish to succeed.
 * The social error check is explicit because isOneClickSocialPostInSuccessState, unlike its email
 * counterpart, carries no error exclusion of its own.
 */
export const selectHaveAllSelectedChannelsPublished = (state: EventOneClickPromoReduxState): boolean => {
  if (!selectCanPublish(state) || !hasAnyChannelPublishedThisSession(state)) {
    return false;
  }

  // A deselected channel drops out of the check unless it failed: a failed email is unticked when its sends run out.
  const hasSocialChannelPublished = state.isSocialSelected
    ? isOneClickSocialPostInSuccessState(state.socialPost, state.socialPostOneCLickState) && !isOneClickSocialPostInErrorState(state.socialPost, state.socialPostOneCLickState)
    : state.socialPostOneCLickState !== OneClickPromoItemState.ERROR;
  const hasEmailChannelPublished = state.isEmailSelected
    ? isOneClickEmailCampaignInSuccessState(state.campaign, state.campaignOneClickState)
    : state.campaignOneClickState !== OneClickPromoItemState.ERROR;

  return hasSocialChannelPublished && hasEmailChannelPublished;
};
